import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Camera, Heart, Loader2, MapPin, Navigation, Pencil, Share2, Trash2, LocateFixed, X, Check } from "lucide-react";
import { useGeolocation } from "@/hooks/use-geolocation";

type Favourite = {
  id: string;
  name: string;
  lat: number;
  lon: number;
  notes: string | null;
  photo_path: string | null;
  share_token: string;
  created_at: string;
  photo_url?: string;
};

type EditState = {
  id: string;
  name: string;
  lat: string;
  lon: string;
  notes: string;
  photoFile: File | null;
};

const photoUrl = (path: string | null) =>
  path ? supabase.storage.from("favourite-spot-photos").getPublicUrl(path).data.publicUrl : undefined;

export const Favourites = () => {
  const { user } = useAuth();
  const { coords, loading: locating, request } = useGeolocation();
  const [items, setItems] = useState<Favourite[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState("");
  const [lat, setLat] = useState("");
  const [lon, setLon] = useState("");
  const [notes, setNotes] = useState("");
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // Edit state
  const [editing, setEditing] = useState<EditState | null>(null);
  const [updating, setUpdating] = useState(false);
  const editFileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!user) return;
    (async () => {
      const { data, error } = await supabase
        .from("favourite_spots")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });
      if (!error && data) {
        setItems(data.map(f => ({ ...f, photo_url: photoUrl(f.photo_path) })));
      }
      setLoading(false);
    })();
  }, [user]);

  const useMyLocation = () => {
    if (coords) {
      setLat(coords.lat.toFixed(5));
      setLon(coords.lon.toFixed(5));
    } else {
      request();
    }
  };

  useEffect(() => {
    if (coords && !lat && !lon) {
      setLat(coords.lat.toFixed(5));
      setLon(coords.lon.toFixed(5));
    }
  }, [coords]); // eslint-disable-line react-hooks/exhaustive-deps

  const reset = () => {
    setName("");
    setLat("");
    setLon("");
    setNotes("");
    setPhotoFile(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const save = async () => {
    if (!user) return;
    if (!name.trim()) return toast.error("Give your spot a name");
    const latNum = Number(lat);
    const lonNum = Number(lon);
    if (!Number.isFinite(latNum) || latNum < -90 || latNum > 90) return toast.error("Latitude must be between -90 and 90");
    if (!Number.isFinite(lonNum) || lonNum < -180 || lonNum > 180) return toast.error("Longitude must be between -180 and 180");

    setSaving(true);
    try {
      let photo_path: string | null = null;
      if (photoFile) {
        if (!photoFile.type.startsWith("image/")) throw new Error("Photo must be an image");
        if (photoFile.size > 8 * 1024 * 1024) throw new Error("Max 8 MB per photo");
        const ext = photoFile.name.split(".").pop() || "jpg";
        photo_path = `${user.id}/${crypto.randomUUID()}.${ext}`;
        const { error: upErr } = await supabase.storage
          .from("favourite-spot-photos")
          .upload(photo_path, photoFile, { contentType: photoFile.type, cacheControl: "3600" });
        if (upErr) throw upErr;
      }

      const { data, error } = await supabase
        .from("favourite_spots")
        .insert({
          user_id: user.id,
          name: name.trim(),
          lat: latNum,
          lon: lonNum,
          notes: notes.trim() || null,
          photo_path,
        })
        .select()
        .single();
      if (error) throw error;

      setItems(prev => [{ ...(data as Favourite), photo_url: photoUrl(data.photo_path) }, ...prev]);
      reset();
      toast.success("Spot saved");
    } catch (err: any) {
      toast.error(err.message || "Couldn't save spot");
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (f: Favourite) => {
    setEditing({
      id: f.id,
      name: f.name,
      lat: f.lat.toString(),
      lon: f.lon.toString(),
      notes: f.notes ?? "",
      photoFile: null,
    });
  };

  const cancelEdit = () => {
    setEditing(null);
    if (editFileRef.current) editFileRef.current.value = "";
  };

  const update = async () => {
    if (!editing || !user) return;
    if (!editing.name.trim()) return toast.error("Give your spot a name");
    const latNum = Number(editing.lat);
    const lonNum = Number(editing.lon);
    if (!Number.isFinite(latNum) || latNum < -90 || latNum > 90) return toast.error("Latitude must be between -90 and 90");
    if (!Number.isFinite(lonNum) || lonNum < -180 || lonNum > 180) return toast.error("Longitude must be between -180 and 180");

    setUpdating(true);
    try {
      const original = items.find(i => i.id === editing.id)!;
      let photo_path = original.photo_path;

      // Upload new photo if selected
      if (editing.photoFile) {
        if (!editing.photoFile.type.startsWith("image/")) throw new Error("Photo must be an image");
        if (editing.photoFile.size > 8 * 1024 * 1024) throw new Error("Max 8 MB per photo");

        // Delete old photo if exists
        if (original.photo_path) {
          await supabase.storage.from("favourite-spot-photos").remove([original.photo_path]);
        }

        const ext = editing.photoFile.name.split(".").pop() || "jpg";
        photo_path = `${user.id}/${crypto.randomUUID()}.${ext}`;
        const { error: upErr } = await supabase.storage
          .from("favourite-spot-photos")
          .upload(photo_path, editing.photoFile, { contentType: editing.photoFile.type, cacheControl: "3600" });
        if (upErr) throw upErr;
      }

      const { data, error } = await supabase
        .from("favourite_spots")
        .update({
          name: editing.name.trim(),
          lat: latNum,
          lon: lonNum,
          notes: editing.notes.trim() || null,
          photo_path,
        })
        .eq("id", editing.id)
        .select()
        .single();
      if (error) throw error;

      setItems(prev =>
        prev.map(i => i.id === editing.id ? { ...(data as Favourite), photo_url: photoUrl(data.photo_path) } : i)
      );
      setEditing(null);
      toast.success("Spot updated");
    } catch (err: any) {
      toast.error(err.message || "Couldn't update spot");
    } finally {
      setUpdating(false);
    }
  };

  const remove = async (f: Favourite) => {
    if (!confirm(`Delete "${f.name}"?`)) return;
    if (f.photo_path) await supabase.storage.from("favourite-spot-photos").remove([f.photo_path]);
    await supabase.from("favourite_spots").delete().eq("id", f.id);
    setItems(prev => prev.filter(x => x.id !== f.id));
  };

  const share = async (f: Favourite) => {
    const url = `${window.location.origin}/spot/${f.share_token}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: f.name, text: `Check out ${f.name} on Swell Atlas`, url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("Share link copied");
      }
    } catch {
      /* user cancelled */
    }
  };

  const directions = (f: Favourite) => {
    const dest = `${f.lat},${f.lon}`;
    const origin = coords ? `${coords.lat},${coords.lon}` : "";
    const url = `https://www.google.com/maps/dir/?api=1&${origin ? `origin=${origin}&` : ""}destination=${dest}&travelmode=driving`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="space-y-6">
      {/* Add spot form */}
      <div className="rounded-3xl border border-border bg-card/80 backdrop-blur p-6">
        <div className="flex items-center gap-2 mb-1">
          <Heart className="h-4 w-4 text-primary" />
          <h3 className="font-display text-xl">Save a favourite spot</h3>
        </div>
        <p className="text-xs text-muted-foreground mb-4">
          Pin a secret reef, your home break, or a strike-mission. Add a photo and share the link with your crew.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="fav-name">Spot name</Label>
            <Input id="fav-name" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Secret Left" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="fav-lat">Latitude</Label>
            <Input id="fav-lat" inputMode="decimal" value={lat} onChange={e => setLat(e.target.value)} placeholder="-33.891" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="fav-lon">Longitude</Label>
            <Input id="fav-lon" inputMode="decimal" value={lon} onChange={e => setLon(e.target.value)} placeholder="151.277" />
          </div>
          <div className="sm:col-span-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={useMyLocation}
              disabled={locating}
              className="w-full"
            >
              {locating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <LocateFixed className="h-3.5 w-3.5" />}
              <span className="ml-1.5">Use my current location</span>
            </Button>
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="fav-notes">Notes (optional)</Label>
            <Textarea
              id="fav-notes"
              rows={2}
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Best on a SW swell, mid tide, light offshore…"
            />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label>Photo (optional)</Label>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={e => setPhotoFile(e.target.files?.[0] ?? null)}
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => fileRef.current?.click()}
              className="w-full justify-start"
            >
              <Camera className="h-4 w-4" />
              <span className="truncate">{photoFile ? photoFile.name : "Choose a photo"}</span>
            </Button>
          </div>
        </div>

        <Button
          onClick={save}
          disabled={saving}
          className="mt-4 w-full bg-gradient-sunset text-accent-foreground hover:opacity-90"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Heart className="h-4 w-4" />}
          <span className="ml-1.5">Save spot</span>
        </Button>
      </div>

      {/* Spots list */}
      <div className="rounded-3xl border border-border bg-card/80 backdrop-blur p-6">
        <h3 className="font-display text-xl mb-4">Your favourite spots ({items.length})</h3>
        {loading ? (
          <div className="text-center py-8"><Loader2 className="h-5 w-5 animate-spin text-primary mx-auto" /></div>
        ) : items.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8">No saved spots yet. Pin your first one above.</p>
        ) : (
          <ul className="space-y-3">
            {items.map(f => (
              <li key={f.id} className="rounded-2xl border border-border bg-background/60 overflow-hidden">
                {/* Edit mode */}
                {editing?.id === f.id ? (
                  <div className="p-4 space-y-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-muted-foreground">Editing spot</span>
                      <button onClick={cancelEdit} className="p-1.5 rounded-full hover:bg-muted transition-colors">
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="space-y-1.5 sm:col-span-2">
                        <Label>Spot name</Label>
                        <Input
                          value={editing.name}
                          onChange={e => setEditing(prev => prev && ({ ...prev, name: e.target.value }))}
                          placeholder="e.g. Secret Left"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label>Latitude</Label>
                        <Input
                          inputMode="decimal"
                          value={editing.lat}
                          onChange={e => setEditing(prev => prev && ({ ...prev, lat: e.target.value }))}
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label>Longitude</Label>
                        <Input
                          inputMode="decimal"
                          value={editing.lon}
                          onChange={e => setEditing(prev => prev && ({ ...prev, lon: e.target.value }))}
                        />
                      </div>
                      <div className="space-y-1.5 sm:col-span-2">
                        <Label>Notes (optional)</Label>
                        <Textarea
                          rows={2}
                          value={editing.notes}
                          onChange={e => setEditing(prev => prev && ({ ...prev, notes: e.target.value }))}
                          placeholder="Best on a SW swell, mid tide, light offshore…"
                        />
                      </div>
                      <div className="space-y-1.5 sm:col-span-2">
                        <Label>Replace photo (optional)</Label>
                        <input
                          ref={editFileRef}
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={e => setEditing(prev => prev && ({ ...prev, photoFile: e.target.files?.[0] ?? null }))}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => editFileRef.current?.click()}
                          className="w-full justify-start"
                        >
                          <Camera className="h-4 w-4" />
                          <span className="truncate">{editing.photoFile ? editing.photoFile.name : "Choose a new photo"}</span>
                        </Button>
                      </div>
                    </div>
                    <div className="flex gap-2 pt-1">
                      <Button
                        onClick={update}
                        disabled={updating}
                        className="flex-1 bg-gradient-sunset text-accent-foreground hover:opacity-90"
                      >
                        {updating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                        <span className="ml-1.5">Save changes</span>
                      </Button>
                      <Button variant="outline" onClick={cancelEdit} className="flex-1">
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  // View mode
                  <>
                    {f.photo_url && (
                      <img src={f.photo_url} alt={f.name} className="w-full aspect-video object-cover" loading="lazy" />
                    )}
                    <div className="p-4 space-y-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <h4 className="font-display text-lg leading-tight truncate">{f.name}</h4>
                          <div className="text-xs text-muted-foreground inline-flex items-center gap-1 mt-0.5">
                            <MapPin className="h-3 w-3 shrink-0" />
                            <span className="truncate">{f.lat.toFixed(3)}, {f.lon.toFixed(3)}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <button
                            onClick={() => startEdit(f)}
                            className="p-1.5 rounded-full text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                            aria-label="Edit spot"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                          <button
                            onClick={() => remove(f)}
                            className="p-1.5 rounded-full text-muted-foreground hover:bg-destructive hover:text-destructive-foreground transition-colors"
                            aria-label="Delete spot"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                      {f.notes && <p className="text-sm text-foreground/80">{f.notes}</p>}
                      <div className="flex flex-wrap gap-2">
                        <Button size="sm" variant="outline" onClick={() => share(f)} className="flex-1 min-w-[8rem]">
                          <Share2 className="h-3.5 w-3.5" /> <span className="ml-1.5">Share</span>
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => directions(f)} className="flex-1 min-w-[8rem]">
                          <Navigation className="h-3.5 w-3.5" /> <span className="ml-1.5">Directions</span>
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};
